/** The Smart Assets page: crypto and Smart Asset views, and the two RGB sections. */

import React, { useState, useEffect, useMemo, useCallback, useRef } from "react";
import { useAuth } from "@/adapters/auth";
import { useNavigation } from "@/adapters/navigation";
import { useDatabase, type Asset, type Transaction } from "@/adapters/database";
import { useSatchelWallet } from "../../../hooks";
import { AssetIcon } from "./AssetIcon";
import { ActionButtons } from "./ActionButtons";
import { RecentTransactions } from "./RecentTransactions";
import { CreditsBanner } from "./CreditsBanner";
import { P2PContactsPanel } from "./P2PContactsPanel";
import { SmartAssetCard, type SmartAssetData, CompositionWizard } from "../SmartAssetsComponents";
import {
  usdToPair,
  getMockCreditsBalance,
  getMockSmartAssets,
  getMockSmartAssetUnits,
} from "../../../mocks";
import type { CompositeSmartAsset } from "../../../types/valueFlows";
import { getComposites, saveComposite } from "../../../data/compositionStore";
import type { BitcoinNetwork } from "../../../types";
import type { ChainConfig } from "../../../types/chainConfig";
import type { FeatureDesignation } from "../../../types/designation";
import { DesignationBadge } from "../../common";
import { ASSET_PATHS } from "../../../config";

export interface NetworkConfig {
  id: BitcoinNetwork;
  name: string;
  displayName: string;
  requiresFaucet: boolean;
  faucetNote?: string;
  designation?: FeatureDesignation;
}

export const BITCOIN_NETWORKS: NetworkConfig[] = [
  {
    id: "regtest",
    name: "Regtest",
    displayName: "Bitcoin (Regtest)",
    requiresFaucet: false,
  },
  {
    id: "mainnet",
    name: "Mainnet",
    displayName: "Bitcoin",
    requiresFaucet: false,
    designation: "coming_soon",
  },
  {
    id: "signet",
    name: "Signet",
    displayName: "Bitcoin (Signet)",
    requiresFaucet: true,
    faucetNote: "Faucet required for test coins",
    designation: "coming_soon",
  },
  {
    id: "testnet",
    name: "Testnet3",
    displayName: "Bitcoin (Testnet3)",
    requiresFaucet: true,
    faucetNote: "Faucet required for test coins",
    designation: "coming_soon",
  },
  {
    id: "testnet4",
    name: "Testnet4",
    displayName: "Bitcoin (Testnet4)",
    designation: "coming_soon",
    requiresFaucet: true,
    faucetNote: "Faucet required for test coins",
  },
];

interface DashboardState {
  assets: Asset[];
  transactions: Transaction[];
  totalBalance: number;
  balanceChange: number;
  isLoading: boolean;
  error: string | null;
}

// Convert satoshis to BTC
const satsToBtc = (sats: number): number => sats / 100_000_000;

export interface DashboardProps {
  assetView?: "crypto" | "smart";
  onAssetViewChange?: (view: "crypto" | "smart") => void;
  enabledChains?: ChainConfig[];
  bitcoinNetwork?: BitcoinNetwork;
  onBitcoinNetworkChange?: (network: BitcoinNetwork) => void;
  /** Demo mode enabled (from siteMetadata.demoMode.enabled) */
  demoModeEnabled?: boolean;
  /** Show demo mode toggle (from siteMetadata.demoMode.showToggle) */
  showDemoToggle?: boolean;
  /** Callback when demo mode is toggled */
  onDemoModeToggle?: (enabled: boolean) => void;
  /** Whether to show P2P contacts panel (hidden on simplified, shown on advanced) */
  showContactsPanel?: boolean;
  /** Whether contacts panel starts expanded (for "View all contacts" navigation) */
  defaultContactsExpanded?: boolean;
  /** Callback when a Smart Asset is opened (navigates to Master Container view) */
  onOpenSmartAsset?: (assetId: string) => void;
  /** Callback when scheduling/auto-payments is opened */
  onOpenScheduling?: () => void;
  /** Callback to navigate to credits page */
  onNavigateToCredits?: () => void;
  /** Callback to open the Smart Assets marketplace in the host app's preferred surface */
  onOpenSmartAssetsMarketplace?: () => void;
  /**
   * Callback when Smart Asset selection changes (for top bar highlight)
   * @see docs/UI_WIREFRAMES.md - Section 2: "selecting a Smart Asset highlights its value/color in the top bar"
   */
  onSelectedAssetChange?: (asset: SmartAssetData | null) => void;
}

// Smart Asset data from centralized mocks (single source of truth)
const MOCK_SMART_ASSETS = getMockSmartAssets();

export const Dashboard: React.FC<DashboardProps> = ({
  assetView: externalAssetView,
  onAssetViewChange,
  enabledChains = [],
  bitcoinNetwork: externalBitcoinNetwork,
  onBitcoinNetworkChange,
  demoModeEnabled = false,
  showDemoToggle = false,
  onDemoModeToggle,
  showContactsPanel = false,
  defaultContactsExpanded = false,
  onOpenSmartAsset,
  onOpenScheduling,
  onNavigateToCredits,
  onOpenSmartAssetsMarketplace,
  onSelectedAssetChange,
}) => {
  const auth = useAuth();
  const navigation = useNavigation();
  const database = useDatabase();
  const wallet = useSatchelWallet();
  const [internalAssetView, setInternalAssetView] = useState<"crypto" | "smart">("crypto");

  // Smart Asset Units accordion state (collapsed by default)
  const [smartAssetUnitsExpanded, setSmartAssetUnitsExpanded] = useState(false);

  // Track value display mode (USD vs PAIR numeraire)
  const [valueDisplayMode, setValueDisplayMode] = useState<"usd" | "pair">("usd");

  // Toggle between USD and PAIR display
  const handleValueDisplayToggle = useCallback(() => {
    setValueDisplayMode((prev) => (prev === "usd" ? "pair" : "usd"));
  }, []);

  // Track selected Smart Asset for click-to-open behavior
  const [selectedSmartAssetId, setSelectedSmartAssetId] = useState<string | null>(null);

  // Track last click time per composite ID for reliable double-click detection
  const compositeClickTimes = useRef<Record<string, number>>({});
  const DOUBLE_CLICK_THRESHOLD = 300; // ms - same as SmartAssetCard

  // Composition wizard state
  const [compositionWizardOpen, setCompositionWizardOpen] = useState(false);
  const [compositeAssets, setCompositeAssets] = useState<CompositeSmartAsset[]>([]);

  // Load composite assets from localStorage on mount
  useEffect(() => {
    setCompositeAssets(getComposites());
  }, []);

  // Notify parent when selected asset changes (for top bar highlight)
  useEffect(() => {
    if (!onSelectedAssetChange) return;

    if (!selectedSmartAssetId) {
      onSelectedAssetChange(null);
      return;
    }

    // Look up asset from primitives first, then composites
    const primitiveAsset = MOCK_SMART_ASSETS.find((a) => a.id === selectedSmartAssetId);
    if (primitiveAsset) {
      onSelectedAssetChange(primitiveAsset);
      return;
    }

    // Check composites - generate ticker and description from composite data
    const composite = compositeAssets.find((c) => c.id === selectedSmartAssetId);
    if (composite) {
      // Convert composite to SmartAssetData for the callback
      const ticker = composite.name.substring(0, 3).toUpperCase();
      const description = `Composite Smart Asset with ${composite.components.length} components`;
      onSelectedAssetChange({
        id: composite.id,
        name: composite.name,
        ticker,
        description,
        category: "composite",
        status: "active",
        governanceEnabled: false,
      });
    }
  }, [selectedSmartAssetId, compositeAssets, onSelectedAssetChange]);

  // Handle click outside Smart Asset cards to deselect
  const handleSmartAssetsContainerClick = useCallback((e: React.MouseEvent) => {
    // Only deselect if clicking on the container, not on a card
    const target = e.target as HTMLElement;
    if (!target.closest("[data-asset-id]")) {
      setSelectedSmartAssetId(null);
    }
  }, []);

  // Handle keyboard events on container (Escape to deselect)
  const handleSmartAssetsContainerKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setSelectedSmartAssetId(null);
    }
  }, []);

  // Check for dashboard warning from onboarding (e.g., wallet already exists)
  const [dashboardWarning, setDashboardWarning] = useState<string | null>(null);

  // Check sessionStorage for warning after mount (handles async timing from onboarding)
  useEffect(() => {
    const warning = sessionStorage.getItem("satchel_dashboard_warning");
    if (warning) {
      setDashboardWarning(warning);
      // Clear so it only shows once
      sessionStorage.removeItem("satchel_dashboard_warning");
    }
  }, []);
  const [networkDropdownOpen, setNetworkDropdownOpen] = useState(false);
  const networkDropdownRef = useRef<HTMLDivElement>(null);
  const networkButtonRef = useRef<HTMLButtonElement>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  // Network state comes from wallet context (synced with API calls)
  // External props can override for controlled component usage
  const bitcoinNetwork = externalBitcoinNetwork ?? wallet.network;
  const setBitcoinNetwork = useCallback(
    (network: BitcoinNetwork) => {
      // Update wallet context (triggers balance refresh with correct network)
      wallet.setNetwork(network);
      // Also call external handler if provided
      if (onBitcoinNetworkChange) {
        onBitcoinNetworkChange(network);
      }
    },
    [wallet, onBitcoinNetworkChange],
  );
  const currentNetworkConfig =
    BITCOIN_NETWORKS.find((n) => n.id === bitcoinNetwork) ?? BITCOIN_NETWORKS[1]; // Default to regtest

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        networkDropdownRef.current &&
        !networkDropdownRef.current.contains(event.target as Node)
      ) {
        setNetworkDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Use external state if provided, otherwise use internal state
  const assetView = externalAssetView ?? internalAssetView;
  const setAssetView = onAssetViewChange ?? setInternalAssetView;

  const [state, setState] = useState<DashboardState>({
    assets: [],
    transactions: [],
    totalBalance: 0,
    balanceChange: 0,
    isLoading: true,
    error: null,
  });

  // Track if initial load is complete to avoid showing loading skeleton on subsequent refreshes
  const [initialLoadComplete, setInitialLoadComplete] = useState(false);

  // Smart Asset Units for Cryptocurrencies tab (token-gated: only transferrable units > 0)
  // Covers Path A (owned SA with governance) and Path B (received units without governance)
  // Non-transferrable units (e.g., TaxManager, BountyForge) are excluded
  const smartAssetUnits = useMemo(() => {
    return MOCK_SMART_ASSETS.filter(
      (sa) => sa.unitsTransferrable !== false && getMockSmartAssetUnits(sa.id) > 0,
    ).map((sa) => ({
      id: sa.id,
      name: sa.name,
      ticker: sa.ticker,
      units: getMockSmartAssetUnits(sa.id),
      governanceEnabled: sa.governanceEnabled,
      iconPath: sa.iconPath,
    }));
  }, []);

  // Helper to check if a chain is enabled (memoized for dependency tracking)
  const isChainEnabled = useCallback(
    (chainId: string): boolean => {
      // If no chains configured yet (empty array), allow all for backward compatibility
      if (enabledChains.length === 0) return true;
      return enabledChains.some((c) => c.id === chainId && c.enabled);
    },
    [enabledChains],
  );

  // Convert SATCHEL API balances to Asset format for display
  // Bitcoin comes from the real RGB infrastructure API, network selected via dropdown
  const satchelAssets = useMemo((): Asset[] => {
    const assets: Asset[] = [];

    // Add Bitcoin if BTC or LN chains are enabled
    // Uses real balance from RGB infrastructure API when connected, otherwise shows 0
    const btcEnabled = isChainEnabled("btc") || isChainEnabled("ln");
    if (btcEnabled) {
      const btcAmount = wallet.bitcoinBalance
        ? satsToBtc(wallet.bitcoinBalance.confirmed + wallet.bitcoinBalance.unconfirmed)
        : 0;
      // TODO: Fetch actual BTC price from price feed
      const btcPrice = 100000; // Placeholder BTC/USD price
      assets.push({
        id: "btc-satchel",
        symbol: "BTC",
        name: currentNetworkConfig.displayName,
        balance: btcAmount,
        value: btcAmount * btcPrice,
        change24h: 0, // TODO: Get from price feed
        chain: "bitcoin",
        decimals: 8,
      });
    }

    // Add RGB assets (only if RGB chain is enabled)
    // Note: RGBBalance provides total, precision, and utxo_balances
    if (isChainEnabled("rgb")) {
      wallet.rgbBalances.forEach((rgb) => {
        assets.push({
          id: `rgb-${rgb.contract_id}`,
          symbol: rgb.ticker,
          name: rgb.name,
          balance: rgb.total,
          value: 0, // RGB assets don't have USD price by default
          change24h: 0,
          chain: "rgb",
          decimals: rgb.precision,
        });
      });
    }

    return assets;
  }, [wallet.bitcoinBalance, wallet.rgbBalances, isChainEnabled, currentNetworkConfig]);

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        // Only show loading skeleton on initial load, not on subsequent refreshes
        // This prevents the accordion from closing when wallet balance updates
        if (!initialLoadComplete) {
          setState((prev) => ({ ...prev, isLoading: true, error: null }));
        }

        // Check authentication
        const session = await auth.getSession();
        if (!session.isAuthenticated || !session.user) {
          navigation.push("/login");
          return;
        }

        // Load mock assets and transactions in parallel (fallback/demo data)
        const [allMockAssets, transactions] = await Promise.all([
          database.getAssets(session.user.id),
          database.getTransactions(session.user.id, 5), // Recent 5 transactions
        ]);

        // Map chain names from mock data to chain IDs used in onboarding
        const chainIdMap: Record<string, string> = {
          ethereum: "eth",
          solana: "sol",
          bitcoin: "btc",
        };

        // Mark all mock assets with disabled status based on chain enablement
        // All assets are shown, but disabled ones are greyed out and excluded from totals
        const allMockAssetsWithStatus = allMockAssets.map((asset) => {
          const chainId = chainIdMap[asset.chain] || asset.chain;
          // For Bitcoin, check if either btc or ln (Lightning) is enabled
          const isEnabled =
            chainId === "btc"
              ? isChainEnabled("btc") || isChainEnabled("ln")
              : isChainEnabled(chainId);
          return { ...asset, disabled: !isEnabled };
        });

        // Merge SATCHEL API assets with mock assets
        // Real SATCHEL assets take priority over mock assets for the same symbol
        // Mock assets fill in for chains that don't have real wallet data yet
        const satchelSymbols = new Set(satchelAssets.map((a) => a.symbol.toUpperCase()));
        const mockAssetsWithoutDuplicates = allMockAssetsWithStatus.filter(
          (mock) => !satchelSymbols.has(mock.symbol.toUpperCase()),
        );
        const mergedAssets = [...satchelAssets, ...mockAssetsWithoutDuplicates];

        // Calculate total balance and change (only from ENABLED assets)
        const enabledAssets = mergedAssets.filter((asset) => !asset.disabled);
        const totalBalance = enabledAssets.reduce((sum, asset) => sum + asset.value, 0);

        // Calculate average 24h change weighted by value (only enabled assets)
        const weightedChange = enabledAssets.reduce((sum, asset) => {
          const weight = asset.value / totalBalance || 0;
          return sum + asset.change24h * weight;
        }, 0);

        setState({
          assets: mergedAssets,
          transactions,
          totalBalance,
          balanceChange: weightedChange,
          isLoading: false,
          error: null,
        });

        // Mark initial load as complete
        if (!initialLoadComplete) {
          setInitialLoadComplete(true);
        }
      } catch (error) {
        console.error("Failed to load dashboard data:", error);
        setState((prev) => ({
          ...prev,
          isLoading: false,
          error: "Failed to load dashboard data",
        }));
      }
    };

    loadDashboardData();
  }, [auth, navigation, database, satchelAssets, isChainEnabled, initialLoadComplete]);

  // Handle asset click navigation
  const handleAssetClick = (asset: Asset) => {
    navigation.push(`/satchel/asset/${asset.symbol.toLowerCase()}`);
  };

  // Handle buy button click. The Smart Assets selector opens the Smart Assets marketplace;
  // the cryptocurrency selector must not route users into that marketplace.
  const handleBuyClick = () => {
    if (assetView === "smart") {
      if (onOpenSmartAssetsMarketplace) {
        onOpenSmartAssetsMarketplace();
        return;
      }

      navigation.push("/satchel/buy");
      return;
    }

    setDashboardWarning(
      "Cryptocurrency Buy is not connected yet. Select Smart Assets to browse the Smart Assets marketplace.",
    );
  };

  if (state.isLoading) {
    return (
      <main className="flex-1 overflow-y-auto p-0 md:p-4 pb-20">
        <div className="animate-pulse">
          <div className="h-8 bg-sa-border rounded mb-6 w-32"></div>
          <div className="card p-5 mb-5">
            <div className="h-4 bg-sa-border rounded mb-2 w-24"></div>
            <div className="h-8 bg-sa-border rounded mb-4 w-48"></div>
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-sa-border rounded-full mr-3"></div>
                    <div>
                      <div className="h-4 bg-sa-border rounded mb-1 w-20"></div>
                      <div className="h-3 bg-sa-border rounded w-16"></div>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="h-4 bg-sa-border rounded mb-1 w-16"></div>
                    <div className="h-3 bg-sa-border rounded w-12"></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </main>
    );
  }

  // Handle local state errors (connection errors are handled at app level in main.tsx)
  // Also check wallet.error for non-connection errors that might slip through
  const displayError =
    state.error ||
    (wallet.error && !wallet.error.includes("API not reachable") ? wallet.error : null);
  if (displayError) {
    // Check if this is a network configuration error
    const isNetworkError = displayError.toLowerCase().includes("network not configured");

    const handleBackToHome = () => {
      // Switch to regtest (default working network) and clear errors
      setBitcoinNetwork("regtest");
      setState((prev) => ({ ...prev, error: null, isLoading: true }));
    };

    return (
      <main className="flex-1 overflow-y-auto p-0 md:p-4 pb-20">
        <h1 className="text-2xl font-bold mb-6">Home</h1>
        <div className="card p-5 mb-5 text-center">
          <div className="text-sa-danger mb-4">Error</div>
          <p className="text-sa-danger mb-4">{displayError}</p>
          {isNetworkError && (
            <p className="text-sa-text-muted mb-4 text-sm">
              The selected network is not available. You can switch to the default test network.
            </p>
          )}
          <div className="flex justify-center gap-3">
            <button
              onClick={handleBackToHome}
              className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-hover transition-colors"
            >
              {isNetworkError ? "Switch to Regtest" : "Back to Home"}
            </button>
            <button
              onClick={() => window.location.reload()}
              className="bg-primary text-white px-4 py-2 rounded-md hover:bg-primary-hover transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </main>
    );
  }

  return (
    <main className="flex-1 overflow-y-auto p-0 md:p-4 pb-20" data-testid="dashboard">
      {/* Dashboard Warning Banner (one-time, dismissible) */}
      {dashboardWarning && (
        <div
          data-testid="dashboard-warning"
          className="mb-4 p-3 bg-sa-warning/10 border border-sa-warning/40 rounded-lg flex items-center justify-between"
        >
          <div className="flex items-center">
            <svg
              className="w-5 h-5 text-sa-warning mr-2 flex-shrink-0"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fillRule="evenodd"
                d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                clipRule="evenodd"
              />
            </svg>
            <span className="text-sm text-sa-warning">{dashboardWarning}</span>
          </div>
          <button
            type="button"
            onClick={() => setDashboardWarning(null)}
            className="text-sa-warning hover:text-sa-warning ml-2"
          >
            <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Balance Card */}
      <div className="card p-5 mb-5">
        <div className="flex items-center gap-2 mb-2">
          <p className="text-sa-text-muted text-base font-medium">SATCHEL Total Value</p>
          <button
            type="button"
            onClick={handleValueDisplayToggle}
            className="flex items-center gap-1 px-2 py-0.5 text-xs font-medium rounded-full bg-sa-surface-hover text-sa-text-muted hover:bg-sa-border transition-colors cursor-pointer"
            title={`Click to show in ${valueDisplayMode === "usd" ? "PAIR" : "USD"}`}
            data-testid="value-mode-indicator"
          >
            <span
              className={valueDisplayMode === "usd" ? "font-bold text-sa-success" : "opacity-50"}
            >
              $
            </span>
            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
              />
            </svg>
            <span className={valueDisplayMode === "pair" ? "font-bold text-gold" : "opacity-50"}>
              PAIR
            </span>
          </button>
        </div>
        <div
          role="button"
          tabIndex={0}
          onClick={handleValueDisplayToggle}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              handleValueDisplayToggle();
            }
          }}
          className="flex items-baseline bg-sa-surface-hover border border-sa-border rounded-xl p-4 mb-2 cursor-pointer hover:bg-sa-border transition-colors"
          title={`Click to show in ${valueDisplayMode === "usd" ? "PAIR" : "USD"}`}
          data-testid="total-value-toggle"
        >
          <h2 className="text-4xl font-bold text-sa-text flex items-center">
            {valueDisplayMode === "usd" ? (
              <>
                <span className="text-sa-success">$</span>
                {state.totalBalance.toLocaleString("en-US", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </>
            ) : (
              <>
                <span className="text-gold">
                  {usdToPair(state.totalBalance).toLocaleString("en-US", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
                <img src={ASSET_PATHS.PAIR_ICON} alt="PAIR" className="w-8 h-8 ml-2" />
              </>
            )}
          </h2>
          <span
            className={`ml-3 text-base font-semibold ${
              state.balanceChange >= 0 ? "text-sa-success" : "text-sa-danger"
            }`}
          >
            {state.balanceChange >= 0 ? "+" : ""}
            {state.balanceChange.toFixed(1)}%
          </span>
        </div>

        {/* Digital Assets */}
        <div className="mt-6 mb-2">
          <div className="flex flex-col items-center mb-3">
            <p className="text-sm font-medium text-sa-text-muted mb-2">Digital Assets</p>
            <div className="w-full md:w-auto inline-flex rounded-full border border-sa-border bg-sa-surface-hover">
              <button
                onClick={() => setAssetView("crypto")}
                data-testid="cryptocurrencies-tab"
                className={`flex-1 md:flex-none px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                  assetView === "crypto"
                    ? "bg-primary text-white"
                    : "text-sa-text hover:bg-sa-surface-hover"
                }`}
              >
                Cryptocurrencies
              </button>
              <button
                onClick={() => setAssetView("smart")}
                data-testid="smart-assets-tab"
                className={`flex-1 md:flex-none px-4 py-2 text-sm font-semibold rounded-full transition-colors ${
                  assetView === "smart"
                    ? "bg-primary text-white"
                    : "text-sa-text hover:bg-sa-surface-hover"
                }`}
              >
                Smart Assets
              </button>
            </div>
          </div>

          {assetView === "crypto" ? (
            <div className="space-y-2">
              {state.assets.length > 0 ? (
                state.assets.map((asset, index) => (
                  <div
                    key={asset.id}
                    role="button"
                    tabIndex={asset.disabled ? -1 : 0}
                    className={`flex items-center justify-between ${
                      asset.disabled
                        ? "cursor-not-allowed opacity-50 bg-sa-surface-hover border border-sa-border"
                        : `cursor-pointer ${
                            index % 2 === 0 ? "bg-orange-500/10" : "bg-sa-warning/10"
                          } hover:bg-orange-500/10 border border-orange-500/40 hover:border-orange-500/40`
                    } transition-all duration-200 rounded-xl p-3`}
                    onClick={() => !asset.disabled && handleAssetClick(asset)}
                    onKeyDown={(e) => {
                      if (!asset.disabled && (e.key === "Enter" || e.key === " ")) {
                        e.preventDefault();
                        handleAssetClick(asset);
                      }
                    }}
                  >
                    <div className="flex items-center">
                      <AssetIcon
                        asset={asset}
                        size="md"
                        className={asset.disabled ? "grayscale" : ""}
                      />
                      <div className="ml-1">
                        <div className="flex items-center gap-1">
                          <p
                            className={`font-semibold ${asset.disabled ? "text-sa-text-muted" : "text-sa-text"}`}
                          >
                            {asset.id === "btc-satchel" ? "Bitcoin" : asset.name}
                          </p>
                          {asset.disabled && (
                            <span className="px-1.5 py-0.5 text-xs font-medium text-sa-text-muted bg-sa-surface-hover rounded">
                              Coming Soon
                            </span>
                          )}
                          {/* Network selector dropdown for Bitcoin */}
                          {asset.id === "btc-satchel" && (
                            <div className="relative ml-1" ref={networkDropdownRef}>
                              <button
                                ref={networkButtonRef}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (!networkDropdownOpen && networkButtonRef.current) {
                                    const rect = networkButtonRef.current.getBoundingClientRect();
                                    setDropdownPosition({
                                      top: rect.bottom + 4,
                                      left: rect.left,
                                    });
                                  }
                                  setNetworkDropdownOpen(!networkDropdownOpen);
                                }}
                                className="flex items-center gap-0.5 px-1.5 py-0.5 text-xs font-medium text-orange-500 bg-orange-500/10 rounded hover:bg-orange-500/10 transition-colors"
                                data-testid="network-selector-button"
                              >
                                <span>{currentNetworkConfig.name}</span>
                                <svg
                                  className={`w-3 h-3 transition-transform ${
                                    networkDropdownOpen ? "rotate-180" : ""
                                  }`}
                                  fill="none"
                                  stroke="currentColor"
                                  viewBox="0 0 24 24"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M19 9l-7 7-7-7"
                                  />
                                </svg>
                              </button>
                              {networkDropdownOpen && (
                                <div
                                  className="fixed z-[9999] w-56 bg-sa-surface border border-sa-border rounded-lg shadow-xl"
                                  style={{
                                    top: dropdownPosition.top,
                                    left: dropdownPosition.left,
                                  }}
                                  data-testid="network-dropdown"
                                  role="listbox"
                                  onClick={(e) => e.stopPropagation()}
                                  onKeyDown={(e) => {
                                    if (e.key === "Escape") {
                                      setNetworkDropdownOpen(false);
                                    }
                                  }}
                                >
                                  {BITCOIN_NETWORKS.filter(
                                    (network) => !demoModeEnabled || network.id !== "mainnet",
                                  ).map((network) => (
                                    <button
                                      key={network.id}
                                      type="button"
                                      disabled={network.designation === "coming_soon"}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        e.preventDefault();
                                        if (network.designation !== "coming_soon") {
                                          setBitcoinNetwork(network.id);
                                          setNetworkDropdownOpen(false);
                                        }
                                      }}
                                      className={`w-full text-left px-3 py-2 text-sm transition-colors first:rounded-t-lg last:rounded-b-lg ${
                                        network.designation === "coming_soon"
                                          ? "opacity-50 cursor-not-allowed text-sa-text-dim"
                                          : bitcoinNetwork === network.id
                                            ? "bg-orange-500/10 text-orange-500"
                                            : "text-sa-text hover:bg-sa-surface-hover"
                                      }`}
                                      data-testid={`network-option-${network.id}`}
                                    >
                                      <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                          <span
                                            className={`font-medium ${network.designation === "coming_soon" ? "line-through" : ""}`}
                                          >
                                            {network.name}
                                          </span>
                                          {network.designation && (
                                            <DesignationBadge
                                              designation={network.designation}
                                              size="sm"
                                            />
                                          )}
                                        </div>
                                        {bitcoinNetwork === network.id &&
                                          network.designation !== "coming_soon" && (
                                            <svg
                                              className="w-4 h-4 text-orange-500"
                                              fill="currentColor"
                                              viewBox="0 0 20 20"
                                            >
                                              <path
                                                fillRule="evenodd"
                                                d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                                clipRule="evenodd"
                                              />
                                            </svg>
                                          )}
                                      </div>
                                      {network.requiresFaucet && (
                                        <p className="text-xs text-sa-warning mt-0.5">
                                          {network.faucetNote}
                                        </p>
                                      )}
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                        <p
                          className={`text-sm ${asset.disabled ? "text-sa-text-dim" : "text-sa-text-muted"}`}
                          data-testid={`asset-balance-${asset.id}`}
                        >
                          {asset.disabled
                            ? "--"
                            : `${asset.balance.toFixed(asset.decimals)} ${asset.symbol}`}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p
                        className={`font-semibold text-lg ${asset.disabled ? "text-sa-text-dim" : "text-sa-text"}`}
                      >
                        {asset.disabled ? "--" : `$${asset.value.toFixed(2)}`}
                      </p>
                      <p
                        className={`text-sm font-medium ${
                          asset.disabled
                            ? "text-sa-text-dim"
                            : asset.change24h > 0
                              ? "text-sa-success"
                              : asset.change24h < 0
                                ? "text-sa-danger"
                                : "text-sa-text-muted"
                        }`}
                      >
                        {asset.disabled
                          ? "--"
                          : asset.change24h !== 0
                            ? `${asset.change24h > 0 ? "+" : ""}${asset.change24h.toFixed(2)}%`
                            : "0.00%"}
                      </p>
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center py-8 text-sa-text-muted bg-sa-surface-hover rounded-xl border border-sa-border">
                  <p className="mb-4 text-base">No assets yet</p>
                  <button
                    onClick={handleBuyClick}
                    className="bg-primary text-white px-6 py-2.5 rounded-lg font-semibold hover:bg-primary-hover transition-colors"
                  >
                    Buy Your First Asset
                  </button>
                </div>
              )}

              {/* Smart Asset Units subsection (token-gated: only shown when user holds units) */}
              {smartAssetUnits.length > 0 && (
                <div className="mt-4" data-testid="smart-asset-units-section">
                  <button
                    type="button"
                    onClick={() => setSmartAssetUnitsExpanded((prev) => !prev)}
                    data-testid="smart-asset-units-toggle"
                    className="w-full flex items-center justify-between py-2 px-1 group"
                  >
                    <div className="flex items-center gap-2">
                      <p className="text-xs font-semibold uppercase tracking-wider text-purple-500">
                        Smart Asset Units
                      </p>
                      <span className="text-[10px] font-medium text-purple-500 bg-purple-500/10 px-1.5 py-0.5 rounded-full">
                        {smartAssetUnits.length}
                      </span>
                    </div>
                    <svg
                      className={`w-4 h-4 text-purple-500 transition-transform duration-200 ${
                        smartAssetUnitsExpanded ? "rotate-180" : ""
                      }`}
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>
                  <div
                    className={`space-y-2 overflow-hidden transition-all duration-200 ${
                      smartAssetUnitsExpanded ? "max-h-[1000px] opacity-100" : "max-h-0 opacity-0"
                    }`}
                    data-testid="smart-asset-units-content"
                  >
                    {smartAssetUnits.map((unit) => (
                      <div
                        key={`sa-unit-${unit.id}`}
                        role="button"
                        tabIndex={0}
                        data-testid={`smart-asset-unit-${unit.id}`}
                        className="flex items-center justify-between cursor-pointer bg-purple-500/10 hover:bg-purple-500/10 border border-purple-500/40 hover:border-purple-500/40 transition-all duration-200 rounded-xl p-3"
                        onClick={() => {
                          if (unit.governanceEnabled) {
                            setAssetView("smart");
                          }
                        }}
                        onKeyDown={(e) => {
                          if (unit.governanceEnabled && (e.key === "Enter" || e.key === " ")) {
                            e.preventDefault();
                            setAssetView("smart");
                          }
                        }}
                      >
                        <div className="flex items-center">
                          {unit.iconPath ? (
                            <img
                              src={unit.iconPath}
                              alt={unit.name}
                              className="w-8 h-8 rounded-full object-contain"
                            />
                          ) : (
                            <div className="w-8 h-8 rounded-full bg-purple-500/10 flex items-center justify-center text-xs font-bold text-purple-500">
                              {unit.ticker.substring(0, 2)}
                            </div>
                          )}
                          <div className="ml-2">
                            <div className="flex items-center gap-1.5">
                              <p className="font-semibold text-sa-text text-sm">{unit.name}</p>
                              {unit.governanceEnabled ? (
                                <span
                                  className="px-1.5 py-0.5 text-[10px] font-medium text-purple-500 bg-purple-500/10 rounded"
                                  data-testid={`sa-unit-governance-badge-${unit.id}`}
                                >
                                  Governance
                                </span>
                              ) : (
                                <span
                                  className="px-1.5 py-0.5 text-[10px] font-medium text-sa-text-muted bg-sa-surface-hover rounded"
                                  data-testid={`sa-unit-no-governance-badge-${unit.id}`}
                                >
                                  Units Only
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-sa-text-muted">
                              {unit.units.toLocaleString()} {unit.ticker}
                            </p>
                          </div>
                        </div>
                        {unit.governanceEnabled && (
                          <svg
                            className="w-4 h-4 text-purple-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div
              className="space-y-4"
              role="region"
              aria-label="Smart Assets"
              onClick={handleSmartAssetsContainerClick}
              onKeyDown={handleSmartAssetsContainerKeyDown}
            >
              {/* SA Credits Banner - full width above Smart Asset cards */}
              <CreditsBanner
                {...getMockCreditsBalance()}
                onNavigateToCredits={onNavigateToCredits}
              />

              {/* Auto Payments & Expected Income - immediately after Credits */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  onOpenScheduling?.();
                }}
                data-testid="auto-payments-income-button"
                className="w-full p-4 mt-3 border border-sa-border rounded-lg bg-sa-surface hover:bg-sa-surface-hover hover:border-primary/40 transition-colors text-left"
              >
                <div className="flex items-center justify-between flex-wrap gap-4">
                  {/* Left: Icon + Title */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary/10 to-sa-success/10 flex items-center justify-center">
                      <svg
                        className="w-5 h-5 text-primary"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div>
                      <p className="text-sm font-semibold text-sa-text">
                        Auto Payments & Expected Income
                      </p>
                      <p className="text-xs text-sa-text-muted">
                        Scheduled transfers & programmatic cashflows
                      </p>
                    </div>
                  </div>

                  {/* Center: Stats */}
                  <div className="flex items-center gap-4 text-sm">
                    <div className="text-center">
                      <p className="text-sa-text-muted text-xs">Burn Rate</p>
                      <p className="font-semibold text-sa-danger flex items-center justify-center">
                        -45.2
                        <img
                          src={ASSET_PATHS.PAIR_ICON}
                          alt="PAIR"
                          className="w-3.5 h-3.5 mx-0.5"
                        />
                        /mo
                      </p>
                    </div>
                    <div className="h-8 w-px bg-sa-surface-hover" />
                    <div className="text-center">
                      <p className="text-sa-text-muted text-xs">Inflow Rate</p>
                      <p className="font-semibold text-sa-success flex items-center justify-center">
                        +120.0
                        <img
                          src={ASSET_PATHS.PAIR_ICON}
                          alt="PAIR"
                          className="w-3.5 h-3.5 mx-0.5"
                        />
                        /mo
                      </p>
                    </div>
                    <div className="h-8 w-px bg-sa-surface-hover" />
                    <div className="text-center">
                      <p className="text-sa-text-muted text-xs">Depletes In</p>
                      <p className="font-semibold text-sa-text">Never</p>
                    </div>
                  </div>

                  {/* Right: View Calendar Button */}
                  <div className="flex items-center gap-2">
                    <span className="text-xs bg-primary/10 text-primary px-2 py-1 rounded-md font-medium">
                      View Calendar
                    </span>
                    <svg
                      className="w-4 h-4 text-sa-text-dim"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M9 5l7 7-7 7"
                      />
                    </svg>
                  </div>
                </div>
              </button>

              {/* Featured Smart Asset Cards */}
              <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                {MOCK_SMART_ASSETS.map((asset) => (
                  <SmartAssetCard
                    key={asset.id}
                    asset={asset}
                    isSelected={selectedSmartAssetId === asset.id}
                    onSelect={setSelectedSmartAssetId}
                    onOpen={(assetId: string) => {
                      if (onOpenSmartAsset) {
                        onOpenSmartAsset(assetId);
                      }
                    }}
                  />
                ))}
              </div>

              <div className="mt-2">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sa-text-muted text-sm mb-0">Composite Smart Assets</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setCompositionWizardOpen(true);
                    }}
                    className="px-3 py-1 text-sm font-medium text-white bg-primary hover:bg-primary-hover rounded-lg transition-colors"
                    data-testid="compose-button"
                  >
                    Compose
                  </button>
                </div>
                <div className="space-y-3">
                  {/* Dynamically created composites - click to select, double-click or Open button to open */}
                  {compositeAssets.map((composite) => {
                    const isSelected = selectedSmartAssetId === composite.id;
                    return (
                      <div
                        key={composite.id}
                        role="button"
                        tabIndex={0}
                        data-asset-id={composite.id}
                        data-selected={isSelected}
                        onClick={(e) => {
                          e.stopPropagation();
                          const now = Date.now();
                          const lastClickTime = compositeClickTimes.current[composite.id] || 0;

                          if (now - lastClickTime < DOUBLE_CLICK_THRESHOLD) {
                            // Double click detected - open the asset
                            if (onOpenSmartAsset) {
                              onOpenSmartAsset(composite.id);
                            }
                            compositeClickTimes.current[composite.id] = 0;
                          } else {
                            // Single click - toggle selection
                            setSelectedSmartAssetId(isSelected ? null : composite.id);
                            compositeClickTimes.current[composite.id] = now;
                          }
                        }}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            // Enter opens the asset
                            if (onOpenSmartAsset) {
                              onOpenSmartAsset(composite.id);
                            }
                          } else if (e.key === " ") {
                            // Space toggles selection
                            e.preventDefault();
                            setSelectedSmartAssetId(isSelected ? null : composite.id);
                          }
                        }}
                        className={`relative p-3 border rounded-lg cursor-pointer transition-all duration-200 ${
                          isSelected
                            ? "border-sa-success bg-sa-success/10 ring-2 ring-sa-success/50"
                            : "border-sa-success/40 bg-sa-success/10 hover:bg-sa-success/10 hover:border-sa-success/40"
                        }`}
                        data-testid={`composite-asset-${composite.id}`}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            <div className="w-8 h-8 rounded-full bg-sa-success/10 flex items-center justify-center mr-3">
                              <svg
                                className="w-4 h-4 text-sa-success"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                              >
                                <path d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2V4zm2 0v12h8V4H6z" />
                                <path d="M8 7h4M8 10h4M8 13h2" />
                              </svg>
                            </div>
                            <div>
                              <p className="text-sm font-medium text-sa-text">{composite.name}</p>
                              <p className="text-xs text-sa-text-muted">
                                {composite.components.length} assets •{" "}
                                {composite.composition_links.length} connections
                              </p>
                            </div>
                          </div>
                          <span className="text-[10px] bg-sa-success/10 text-sa-success px-1.5 py-0.5 rounded-md">
                            Active
                          </span>
                        </div>

                        {/* Open Button - shown when selected */}
                        {isSelected && (
                          <div className="absolute inset-0 flex items-center justify-center bg-black/10 rounded-lg">
                            <button
                              data-testid={`composite-open-${composite.id}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (onOpenSmartAsset) {
                                  onOpenSmartAsset(composite.id);
                                }
                              }}
                              className="px-6 py-2 bg-primary hover:bg-primary-hover text-white font-semibold rounded-lg shadow-lg transition-colors"
                            >
                              Open
                            </button>
                          </div>
                        )}

                        {/* Double-click hint when selected */}
                        {isSelected && (
                          <div className="absolute bottom-1 right-2 text-[10px] text-sa-success">
                            Double-click to open
                          </div>
                        )}
                      </div>
                    );
                  })}

                  {/* Placeholder when no composites */}
                  {compositeAssets.length === 0 && (
                    <div className="p-3 border border-dashed border-sa-border rounded-lg bg-sa-surface-hover">
                      <p className="text-sm text-sa-text-muted text-center">
                        No composites yet. Click "Compose" to create one.
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* P2P Contacts Panel - shown only on Advanced view, outside assetView tabs */}
      {showContactsPanel && (
        <P2PContactsPanel
          bitcoinNetwork={bitcoinNetwork}
          demoModeEnabled={demoModeEnabled}
          showDemoToggle={showDemoToggle}
          onDemoModeToggle={onDemoModeToggle}
          defaultExpanded={defaultContactsExpanded}
          className="mt-4"
        />
      )}

      {/* Action Buttons - Send hidden on Smart Assets tab (transfers done via detail view) */}
      <ActionButtons hideSend={assetView === "smart"} onBuyClick={handleBuyClick} />

      {/* Recent Transactions */}
      <RecentTransactions transactions={state.transactions} />

      {/* Composition Wizard Modal */}
      {compositionWizardOpen && (
        <CompositionWizard
          availableAssets={[
            ...MOCK_SMART_ASSETS,
            ...compositeAssets.map((c) => ({
              id: c.id,
              name: c.name,
              ticker: c.name.substring(0, 3).toUpperCase(),
              description: `Composite Smart Asset created ${new Date(c.created_at).toLocaleDateString()}`,
              category: "composite" as const,
              status: "demo" as const,
              governanceEnabled: false,
            })),
          ]}
          onClose={() => setCompositionWizardOpen(false)}
          onComplete={(composite) => {
            saveComposite(composite);
            setCompositeAssets([...compositeAssets, composite]);
            setCompositionWizardOpen(false);
          }}
        />
      )}
    </main>
  );
};
